import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TmdxAvatar, Chip, Tooltip } from '@ds';
import { TableSwitch, TableColumnType } from '@ui';
import { EmployeeComposed, EmployeeListFilters, EmployeeRole, EmployeeStatus } from '@/api/apis/resources';
import { CSShiftTimeGroup } from '@/api/apis/resources/schedule';
import { getFullName, getRoleNames } from '@/utils/helpers';
import { NameByValue } from '@/types';
import { roleNames } from '@/constants/user.constants';
import { shiftTimeGroupNames } from '@/constants/schedule.constants';
import { col } from '../../utils';

export const EMPLOYEE_ROLES_SELECT_LIST: NameByValue<EmployeeRole>[] = Object.values(EmployeeRole).map((role) => ({
  value: role,
  name: roleNames[role],
}));

export const EMPLOYEES_LIST_FILTERS_BASE: EmployeeListFilters = {
  employeeIds: [],
  roles: [],
  baseLocation: [],
  status: EmployeeStatus.All,
  isContractor: false,
  shiftTimeGroup: [],
};

export const SHIFT_TIME_GROUPS_SELECT_LIST: NameByValue<CSShiftTimeGroup>[] = [
  { value: CSShiftTimeGroup.GroupA, name: 'Group A (11:00 - 10:59)' },
  { value: CSShiftTimeGroup.GroupB, name: 'Group B (15:00 - 14:59)' },
];

const EMPLOYEE_COLUMNS: TableColumnType[] = [
  col('name', 'TMDX Employee Name', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('role', 'Role', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('position', 'Position', {
    sortable: true,
    minWidthClassName: 'min-w-60',
  }),
  col('trialPrograms', 'Trial Program', {
    sortable: false,
    minWidthClassName: 'min-w-40',
  }),
  col('shiftTimeGroup', 'Shift Time Group', {
    sortable: false,
    minWidthClassName: 'min-w-32',
  }),
  col('locationName', 'Base Location', {
    sortable: false,
    minWidthClassName: 'min-w-80',
  }),
  col('email', 'Email', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('phone', 'Phone', {
    sortable: false,
    minWidthClassName: 'min-w-60',
  }),
  col('isActive', 'Active', {
    sortable: false,
    minWidthClassName: 'min-w-28',
  }),
];

export const getEmployeesTableColumns = (): TableColumnType[] => {
  return [...EMPLOYEE_COLUMNS];
};

type GetRenderEmployeesTableCellProps = {
  type: string;
  hasEmployeeEditPermission: boolean;
  hasCSEditPermission: boolean;
  hasCLEditPermission: boolean;
  onIsActiveChanged: (employee: EmployeeComposed) => void;
};

export const getRenderEmployeesTableCell =
  ({
    type,
    hasCSEditPermission,
    hasCLEditPermission,
    hasEmployeeEditPermission,
    onIsActiveChanged,
  }: GetRenderEmployeesTableCellProps) =>
  (employee: EmployeeComposed, columnKey: Key): ReactNode => {
    const {
      id,
      firstName,
      lastName,
      profilePhoto,
      roles,
      position,
      email,
      phone,
      baseLocation,
      isActive,
      trialPrograms,
      shiftTimeGroup,
    } = employee;

    const canEditCS = hasCSEditPermission && roles.includes(EmployeeRole.ClinicalSpecialist);
    const canEditCL = hasCLEditPermission && roles.includes(EmployeeRole.ClinicalLead);

    const canEditEmployee = hasEmployeeEditPermission || canEditCS || canEditCL;

    const employeeRoles = getRoleNames(roles);

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/users/${type}s/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" name={getFullName(firstName, lastName)} src={profilePhoto} />
            <span className="text-m-m">{getFullName(firstName, lastName)}</span>
          </Link>
        );
      case 'role':
        return (
          <div className="flex items-center gap-2 truncate">
            {employeeRoles.map((role, index) => {
              if (!index) {
                return <Chip key={`main-${role}`}>{role}</Chip>;
              }

              if (index === 1) {
                const slicedItems = employeeRoles.slice(index);
                const rolesName = slicedItems.map((employeeRole) => employeeRole).join(', ');

                return (
                  <Tooltip placement="top" className="max-w-64" content={rolesName} key="additionalRolesCount">
                    <Chip>{`+${employeeRoles.length - index}`}</Chip>
                  </Tooltip>
                );
              }

              return null;
            })}
          </div>
        );
      case 'position':
        return (
          <div className="flex items-center gap-2">
            <span className="text-m-m">{position}</span>
          </div>
        );
      case 'trialPrograms':
        return (
          <div className="flex items-center gap-2">
            <span className="text-m-m">{trialPrograms?.map((tp) => tp.name).join('; ') || '-'}</span>
          </div>
        );
      case 'shiftTimeGroup':
        return <span className="text-m-r">{shiftTimeGroup ? shiftTimeGroupNames[shiftTimeGroup] : '-'}</span>;
      case 'locationName':
        return (
          <div className="flex items-center gap-2">
            <span className="text-m-m">{baseLocation?.name ?? '-'}</span>
          </div>
        );
      case 'email':
        return <span className="text-m-r">{email}</span>;
      case 'phone':
        return <span className="text-m-r">{phone}</span>;
      case 'isActive':
        return (
          <TableSwitch isActive={isActive} isDisabled={!canEditEmployee} action={() => onIsActiveChanged(employee)} />
        );
      default:
        return null;
    }
  };
